import {
  Paragraph,
  TextRun,
  ImageRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  AlignmentType,
} from "docx";

const NONE = {
  style: BorderStyle.NONE,
  size: 0,
  color: "FFFFFF",
};

const NO_BORDERS = {
  top: NONE,
  bottom: NONE,
  left: NONE,
  right: NONE,
};

const TABLE_NO_BORDERS = {
  ...NO_BORDERS,
  insideH: NONE,
  insideV: NONE,
};

function base64ToUint8Array(dataUrl) {
  const base64 = dataUrl.split(",")[1];
  const binary = atob(base64);

  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
}

function createWeatherChartImage({
  weatherData = [],
  startTime = null,
  endTime = null,
}) {
  if (!Array.isArray(weatherData) || weatherData.length === 0) {
    return null;
  }

  const width = 1000;
  const height = 360;

  const canvas = document.createElement("canvas");

  // Higher resolution for DOCX
  canvas.width = width * 2;
  canvas.height = height * 2;

  const ctx = canvas.getContext("2d");

  ctx.scale(2, 2);

  // Background
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);

  const left = 60;
  const right = 25;
  const top = 40;
  const bottom = 85;

  const graphWidth = width - left - right;
  const graphHeight = height - top - bottom;

  const temperatures = weatherData
    .map((item) => Number(item.temperature))
    .filter(Number.isFinite);

  if (!temperatures.length) {
    return null;
  }

  let minTemp = Math.floor(Math.min(...temperatures)) - 2;
  let maxTemp = Math.ceil(Math.max(...temperatures)) + 2;

  if (minTemp === maxTemp) {
    minTemp -= 1;
    maxTemp += 1;
  }

  // --------------------------------------------------
  // Helpers
  // --------------------------------------------------

  const xForIndex = (index) => {
    if (weatherData.length === 1) {
      return left + graphWidth / 2;
    }

    return (
      left +
      (index / (weatherData.length - 1)) * graphWidth
    );
  };

  const yForTemperature = (temperature) => {
    return (
      top +
      ((maxTemp - temperature) /
        (maxTemp - minTemp)) *
        graphHeight
    );
  };

  const getHour = (item) => {
    if (item.hour) {
      return Number(
        String(item.hour).split(":")[0]
      );
    }

    if (item.time) {
      return new Date(item.time).getHours();
    }

    return null;
  };

  const selectedStartHour = startTime
    ? Number(startTime.split(":")[0])
    : null;

  const selectedEndHour = endTime
    ? Number(endTime.split(":")[0])
    : selectedStartHour;

  // --------------------------------------------------
  // Selected inspection area
  // --------------------------------------------------

  if (
    Number.isFinite(selectedStartHour) &&
    Number.isFinite(selectedEndHour)
  ) {
    const selectedIndexes = weatherData
      .map((item, index) => ({
        index,
        hour: getHour(item),
      }))
      .filter(
        ({ hour }) =>
          Number.isFinite(hour) &&
          hour >=
            Math.min(
              selectedStartHour,
              selectedEndHour
            ) &&
          hour <=
            Math.max(
              selectedStartHour,
              selectedEndHour
            )
      );

    if (selectedIndexes.length) {
      const first =
        selectedIndexes[0].index;

      const last =
        selectedIndexes[
          selectedIndexes.length - 1
        ].index;

      const x1 = xForIndex(first);
      const x2 =
        first === last
          ? x1 +
            graphWidth /
              Math.max(
                weatherData.length - 1,
                1
              )
          : xForIndex(last);

      ctx.fillStyle =
        "rgba(189,189,189,0.35)";

      ctx.fillRect(
        x1,
        top,
        x2 - x1,
        graphHeight
      );
    }
  }

  // --------------------------------------------------
  // Grid + temperature labels
  // --------------------------------------------------

  ctx.font = "12px Arial";
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";

  const gridLines = 5;

  for (let i = 0; i <= gridLines; i++) {
    const ratio = i / gridLines;

    const y =
      top + ratio * graphHeight;

    const value =
      maxTemp -
      ratio * (maxTemp - minTemp);

    ctx.beginPath();

    ctx.strokeStyle = "#e5e5e5";
    ctx.lineWidth = 1;

    ctx.moveTo(left, y);
    ctx.lineTo(width - right, y);

    ctx.stroke();

    ctx.fillStyle = "#f44336";

    ctx.fillText(
      `${value.toFixed(1)}`,
      left - 10,
      y
    );
  }

  // --------------------------------------------------
  // Temperature line
  // --------------------------------------------------

  ctx.beginPath();

  ctx.strokeStyle = "#ff3b3b";
  ctx.lineWidth = 3;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  let started = false;

  weatherData.forEach((item, index) => {
    const temperature =
      Number(item.temperature);

    if (!Number.isFinite(temperature)) {
      return;
    }

    const x = xForIndex(index);
    const y =
      yForTemperature(temperature);

    if (!started) {
      ctx.moveTo(x, y);
      started = true;
    } else {
      ctx.lineTo(x, y);
    }
  });

  ctx.stroke();

  // --------------------------------------------------
  // Hours
  // --------------------------------------------------

  ctx.font = "11px Arial";
  ctx.fillStyle = "#555";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";

  weatherData.forEach((item, index) => {
    const x = xForIndex(index);

    const hour =
      item.hour?.split(":")[0] ??
      "";

    ctx.fillText(
      hour,
      x,
      top + graphHeight + 10
    );
  });

  // --------------------------------------------------
  // Wind arrows + speed
  // --------------------------------------------------

  weatherData.forEach((item, index) => {
    const x = xForIndex(index);

    const direction =
      Number(item.windDirection);

    const speed =
      Number(item.windSpeed);

    const arrowY =
      top + graphHeight + 38;

    if (Number.isFinite(direction)) {
      ctx.save();

      ctx.translate(x, arrowY);

      ctx.rotate(
        (direction * Math.PI) / 180
      );

      ctx.fillStyle = "#57b847";
      ctx.font = "17px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      ctx.fillText("↑", 0, 0);

      ctx.restore();
    }

    if (Number.isFinite(speed)) {
      ctx.fillStyle = "#666";
      ctx.font = "10px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";

      ctx.fillText(
        speed.toFixed(0),
        x,
        arrowY + 13
      );
    }
  });

  // Units
  ctx.fillStyle = "#f44336";
  ctx.font = "bold 11px Arial";
  ctx.textAlign = "left";

  ctx.fillText(
    "°C",
    8,
    top + graphHeight / 2
  );

  ctx.fillStyle = "#777";

  ctx.fillText(
    "m/s",
    width - 45,
    height - 12
  );

  return canvas.toDataURL("image/png");
}

export function renderWeatherBlockDocx({
  data = {},
  children,
}) {
  if (!children) return;

  const weatherData =
    data.dayWeather ||
    data.weatherData ||
    [];

  const date = data.date || null;

  const startTime =
    data.startTime || null;

  const endTime =
    data.endTime || null;

  // --------------------------------------------------
  // Heading
  // --------------------------------------------------

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: "Ulkoilman olosuhteet",
          bold: true,
          size: 22,
          color: "04aa00",
        }),
      ],
      spacing: {
        before: 120,
        after: 100,
      },
    })
  );

  // --------------------------------------------------
  // Date / selected time
  // --------------------------------------------------

  if (date) {
    const formattedDate =
      new Date(
        `${date}T12:00:00`
      ).toLocaleDateString(
        "fi-FI"
      );

    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: formattedDate,
            bold: true,
            size: 18,
          }),

          ...(startTime
            ? [
                new TextRun({
                  text: `   Tarkastusaika: ${startTime}${
                    endTime &&
                    endTime !== startTime
                      ? ` – ${endTime}`
                      : ""
                  }`,
                  size: 16,
                  color: "666666",
                }),
              ]
            : []),
        ],

        spacing: {
          after: 100,
        },
      })
    );
  }

  // --------------------------------------------------
  // Graph
  // --------------------------------------------------

  const chartImage =
    createWeatherChartImage({
      weatherData,
      startTime,
      endTime,
    });

  if (chartImage) {
    children.push(
      new Paragraph({
        children: [
          new ImageRun({
            data:
              base64ToUint8Array(
                chartImage
              ),

            transformation: {
              width: 650,
              height: 234,
            },

            type: "png",
          }),
        ],

        alignment:
          AlignmentType.CENTER,

        spacing: {
          after: 140,
        },
      })
    );
  }

  // --------------------------------------------------
  // Selected weather values
  // --------------------------------------------------

  const info = [
    ["Lämpötila", data.temperature, "°C"],
    ["Kosteus", data.humidity, "%"],
    ["Tuuli", data.windSpeed, "m/s"],
    [
      "Tuulen suunta",
      data.windDirection,
      "",
    ],
  ];

  const cells = info.map(
    ([label, value, unit]) =>
      new TableCell({
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: label,
                bold: true,
                size: 15,
              }),
            ],
          }),

          new Paragraph({
            children: [
              new TextRun({
                text:
                  value != null &&
                  value !== ""
                    ? `${value} ${unit}`.trim()
                    : "-",

                size: 17,
              }),
            ],
          }),
        ],

        borders: NO_BORDERS,
      })
  );

  children.push(
    new Table({
      rows: [
        new TableRow({
          children: cells,
        }),
      ],

      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },

      borders:
        TABLE_NO_BORDERS,
    })
  );

  children.push(
    new Paragraph({
      children: [
        new TextRun(""),
      ],

      spacing: {
        after: 160,
      },
    })
  );
}